import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserSession } from '../contexts/UserContext.js';
import { useUserCache } from '../contexts/UserCacheContext.js';
import { isDevelopment, getApiUrl } from '../utils/environment.js';
import './GodPanel.css';
import cloud from '../Ui/cloud-server.png';
import back from '../Ui/back.webp';
import collapseIcon from '../Ui/collapse1.png';
import expandIcon from '../Ui/expand1.png';


const GodManagement = ({ goBack }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { userSession } = useUserSession();
  const userCache = useUserCache();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('users');
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    authorizationGrade: '',
    planType: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [cachedToken, setCachedToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Track loaded user IDs to prevent duplicates
  const [loadedUserIds, setLoadedUserIds] = useState(new Set());
  
  // Cache for referrer lookups to prevent duplicate API calls
  const [referrerCache, setReferrerCache] = useState(new Map());
  
  // Feedback state
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackHasMore, setFeedbackHasMore] = useState(true);
  const [feedbackFilters, setFeedbackFilters] = useState({
    resolved: '',
    feedbackType: ''
  });

  const apiUrl = getApiUrl();
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();

  // Cached token management
  const getToken = async () => {
    const now = Date.now();
    const TOKEN_REFRESH_BUFFER = 60 * 1000; // Refresh 1 minute before actual expiry
    
    // Return cached token if still valid (with 1-minute buffer to prevent edge-case expiry during API calls)
    if (cachedToken && tokenExpiry > (now + TOKEN_REFRESH_BUFFER)) {
      return cachedToken;
    }
    
    try {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'read:users write:users'
      });
      
    // Cache token for 5 minutes
      setCachedToken(token);
      setTokenExpiry(now + 5 * 60 * 1000);
      
      return token;
    } catch (error) {
      throw error;
    }
  };

  // Mock users for localhost development (using the same ones from LocalDevPanel)
  const getMockUsers = () => [
    {
      uniqueId: 'mock-god-001',
      auth0Id: 'google-oauth2|mock-god-001',
      email: 'mockgod@example.com',
      givenName: 'Mock',
      familyName: 'God',
      authorizationGrade: 'God',
      planType: 'Elite',
      hasPaid: true,
      phoneNumber: '+1234567890',
      country: 'United States',
      reasonToJoin: 'Testing god privileges and system functionality',
      termsAccepted: true,
      referralCount: 25,
      legacyCoins: 1000,
      referralCode: 'GODTEST',
      referredBy: null,
      createdAt: '2024-01-15T10:30:00Z',
      isEmailVerified: true,
      phoneVerified: true,
      earlyAdopter: true
    },
    {
      uniqueId: 'mock-immortal-001',
      auth0Id: 'auth0|mock-immortal-001',
      email: 'mockimmortal@example.com',
      givenName: 'Mock',
      familyName: 'Immortal',
      authorizationGrade: 'Immortal',
      planType: 'Elite',
      hasPaid: true,
      phoneNumber: '+1987654321',
      country: 'United Kingdom',
      reasonToJoin: 'Testing immortal features and advanced functionality',
      termsAccepted: true,
      referralCount: 12,
      legacyCoins: 500,
      referralCode: 'IMMORTEST',
      referredBy: 'mock-god-001',
      createdAt: '2024-02-01T14:20:00Z',
      isEmailVerified: true,
      phoneVerified: true,
      earlyAdopter: true
    },
    {
      uniqueId: 'mock-mortal-001',
      auth0Id: 'linkedin|mock-mortal-001',
      email: 'mockmortal@example.com',
      givenName: 'Mock',
      familyName: 'Mortal',
      authorizationGrade: 'Mortal',
      planType: 'Elite',
      hasPaid: true,
      phoneNumber: '+1555123456',
      country: 'Canada',
      reasonToJoin: 'I want to live forever and preserve my consciousness for future generations',
      termsAccepted: true,
      referralCount: 3,
      legacyCoins: 150,
      referralCode: 'MORTTEST',
      referredBy: 'mock-immortal-001',
      createdAt: '2024-03-10T16:45:00Z',
      isEmailVerified: true,
      phoneVerified: true,
      earlyAdopter: false
    },
    {
      uniqueId: 'mock-unpaid-001',
      auth0Id: 'facebook|mock-unpaid-001',
      email: 'mockunpaid@example.com',
      givenName: 'Mock',
      familyName: 'Unpaid',
      authorizationGrade: 'Mortal',
      planType: 'Unassigned',
      hasPaid: false,
      phoneNumber: '+1666777888',
      country: 'Australia',
      reasonToJoin: 'Still deciding if this is worth the investment',
      termsAccepted: false,
      referralCount: 0,
      legacyCoins: 0,
      referralCode: null,
      referredBy: 'mock-mortal-001',
      createdAt: '2024-04-01T12:00:00Z',
      isEmailVerified: false,
      phoneVerified: false,
      earlyAdopter: false
    },
    {
      uniqueId: 'mock-restricted-001',
      auth0Id: 'auth0|mock-restricted-001',
      email: 'mockrestricted@example.com',
      givenName: 'Mock',
      familyName: 'Restricted',
      authorizationGrade: 'Restricted',
      planType: 'Basic',
      hasPaid: true,
      phoneNumber: '+1777888999',
      country: 'Germany',
      reasonToJoin: 'Had some issues but trying to resolve them',
      termsAccepted: true,
      referralCount: 1,
      legacyCoins: 50,
      referralCode: 'RESTTEST',
      referredBy: null,
      createdAt: '2024-02-20T11:10:00Z',
      isEmailVerified: true,
      phoneVerified: true,
      earlyAdopter: false
    }
  ];

  // Mock feedbacks for localhost development
  const getMockFeedbacks = () => [
    {
      _id: 'mock-feedback-001',
      uniqueId: 'mock-god-001',
      userEmail: 'mockgod@example.com',
      userName: 'Mock God',
      feedbackType: 'feature',
      feedbackText: 'I think it would be great to have a dark mode option for the interface. My eyes get tired after long sessions, and a dark theme would really help with that.',
      isResolved: false,
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      createdAt: '2024-11-20T14:30:00Z'
    },
    {
      _id: 'mock-feedback-002',
      uniqueId: 'mock-immortal-001',
      userEmail: 'mockimmortal@example.com',
      userName: 'Mock Immortal',
      feedbackType: 'bug',
      feedbackText: 'When I try to upload a large video file (over 100MB), the upload progress bar gets stuck at 87% and never completes. I\'ve tried multiple times with different videos and the same thing happens.',
      isResolved: true,
      adminNotes: 'Fixed in version 2.1.3 - increased timeout limits and improved chunked upload handling',
      resolvedBy: 'mock-god-001',
      resolvedAt: '2024-11-22T10:15:00Z',
      createdAt: '2024-11-18T09:45:00Z'
    },
    {
      _id: 'mock-feedback-003',
      uniqueId: 'mock-mortal-001',
      userEmail: 'mockmortal@example.com',
      userName: 'Mock Mortal',
      feedbackType: 'general',
      feedbackText: 'Just wanted to say that I absolutely love this platform! The voice replication feature is mind-blowing. My family is already excited about being able to interact with my digital consciousness in the future.',
      isResolved: true,
      adminNotes: 'Positive feedback - shared with the team!',
      resolvedBy: 'mock-god-001',
      resolvedAt: '2024-11-19T16:20:00Z',
      createdAt: '2024-11-19T11:30:00Z'
    },
    {
      _id: 'mock-feedback-004',
      uniqueId: 'mock-unpaid-001',
      userEmail: 'mockunpaid@example.com',
      userName: 'Mock Unpaid',
      feedbackType: 'question',
      feedbackText: 'How secure is my data? I want to make sure that my personal information, conversations, and media are encrypted and won\'t be accessible to unauthorized parties. Can you provide more details about your security measures?',
      isResolved: false,
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      createdAt: '2024-11-25T13:15:00Z'
    },
    {
      _id: 'mock-feedback-005',
      uniqueId: 'mock-restricted-001',
      userEmail: 'mockrestricted@example.com',
      userName: 'Mock Restricted',
      feedbackType: 'improvement',
      feedbackText: 'The mobile app could use some optimization. It drains my battery pretty quickly, especially when recording voice samples. Maybe you could add a low-power mode?',
      isResolved: false,
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      createdAt: '2024-11-24T18:45:00Z'
    },
    {
      _id: 'mock-feedback-006',
      uniqueId: 'mock-god-001',
      userEmail: 'mockgod@example.com',
      userName: 'Mock God',
      feedbackType: 'bug',
      feedbackText: 'The referral tracking seems to be off. I referred three people this week, but my dashboard only shows two referrals. The missing one is my colleague who signed up on Tuesday.',
      isResolved: false,
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      createdAt: '2024-11-26T08:20:00Z'
    },
    {
      _id: 'mock-feedback-007',
      uniqueId: 'mock-immortal-001',
      userEmail: 'mockimmortal@example.com',
      userName: 'Mock Immortal',
      feedbackType: 'feature',
      feedbackText: 'It would be amazing if we could export our conversation history as a PDF or text file. I\'d like to keep offline backups of my training data.',
      isResolved: true,
      adminNotes: 'Added export feature in Settings > Data Export',
      resolvedBy: 'mock-god-001',
      resolvedAt: '2024-11-23T14:40:00Z',
      createdAt: '2024-11-21T10:10:00Z'
    },
    {
      _id: 'mock-feedback-008',
      uniqueId: 'mock-mortal-001',
      userEmail: 'mockmortal@example.com',
      userName: 'Mock Mortal',
      feedbackType: 'improvement',
      feedbackText: 'The voice cloning feature is great, but it would be even better if we could adjust the tone or emotion of responses. Like having a "formal" mode vs "casual" mode for different situations.',
      isResolved: false,
      adminNotes: '',
      resolvedBy: null,
      resolvedAt: null,
      createdAt: '2024-11-23T15:55:00Z'
    }
  ];

  useEffect(() => {
    // Debounce filter changes to prevent too many API calls
    const timeoutId = setTimeout(() => {
      // Reset pagination when filters change
      setCurrentPage(1);
      setUsers([]);
      setLoadedUserIds(new Set());
      setReferrerCache(new Map()); // Clear referrer cache on filter change
      setHasMore(true);
      loadUsers(true);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Infinite scroll effect with improved throttling for high-traffic scenarios
  useEffect(() => {
    let scrollTimeout;
    let isScrolling = false;
    
    const handleScroll = () => {
      if (isScrolling || loadingMore || !hasMore) return; // Enhanced throttling
      
      isScrolling = true;
      
      scrollTimeout = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
        // Trigger load more when user is 300px from bottom for better UX with thousands of users
        if (scrollTop + clientHeight >= scrollHeight - 300) {
          loadMoreUsers();
        }
        isScrolling = false;
      }, 150); // Increased throttle for production scale
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [loadingMore, hasMore]);

  // Optimized single-pass filter function
  const filterUsers = (users, filterConfig) => {
    const searchLower = filterConfig.search?.toLowerCase();
    
    return users.filter(user => {
      // Search filter (short-circuit if no search)
      if (searchLower) {
        const matchesSearch = 
          user.email?.toLowerCase().includes(searchLower) ||
          user.givenName?.toLowerCase().includes(searchLower) ||
          user.familyName?.toLowerCase().includes(searchLower) ||
          user.uniqueId?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }
      
      // Authorization grade filter
      if (filterConfig.authorizationGrade && 
          user.authorizationGrade !== filterConfig.authorizationGrade) {
        return false;
      }
      
      // Plan type filter
      if (filterConfig.planType && user.planType !== filterConfig.planType) {
        return false;
      }
      
      return true;
    });
  };

  const loadUsersForPage = async (pageNumber) => {
    try {
      setError(''); // Clear previous errors
      
      // For localhost in dev mode, use mock users instead of API
      if (allowDevBypass) {
        const mockUsers = getMockUsers();
        
        // Apply filters to mock users - OPTIMIZED: single-pass filter
        const filteredUsers = filterUsers(mockUsers, filters);

        // Implement pagination for mock users
        const limitToUse = pageNumber === 1 ? 4 : 3; // First page: 4 users, subsequent: 3 users
        const startIndex = pageNumber === 1 ? 0 : 4 + (pageNumber - 2) * 3;
        const endIndex = startIndex + limitToUse;
        
        const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
        
        // Robust duplicate prevention with double-checking
        const newUsers = paginatedUsers.filter(u => !loadedUserIds.has(u.uniqueId));
        if (newUsers.length > 0) {
          setUsers(prev => {
            // Double-check within setter to prevent race conditions
            const finalNewUsers = newUsers.filter(u => !prev.some(existing => existing.uniqueId === u.uniqueId));
            return [...prev, ...finalNewUsers];
          });
          setLoadedUserIds(prev => new Set([...prev, ...newUsers.map(u => u.uniqueId)]));
        }
        
        setHasMore(endIndex < filteredUsers.length);
        return;
      }

      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'read:users write:users'
      });

      const limitToUse = pageNumber === 1 ? 4 : 3; // First page: 4 users, subsequent: 3 users
     
      const params = new URLSearchParams({
        page: pageNumber,
        limit: limitToUse,
        ...filters
      });

      // Ensure no double slashes in URL
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const finalUrl = `${baseUrl}/api/god/users?${params}`;

      const response = await fetch(finalUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.users) {
          // Robust duplicate prevention with double-checking for production scale
          const newUsers = data.users.filter(u => !loadedUserIds.has(u.uniqueId));
          if (newUsers.length > 0) {
            setUsers(prev => {
              // Double-check within setter to prevent race conditions
              const finalNewUsers = newUsers.filter(u => !prev.some(existing => existing.uniqueId === u.uniqueId));
              return [...prev, ...finalNewUsers];
            });
            setLoadedUserIds(prev => new Set([...prev, ...newUsers.map(u => u.uniqueId)]));
          }
          
          // Update pagination info
          if (data.pagination) {
            setHasMore(data.pagination.currentPage < data.pagination.totalPages);
          } else {
            setHasMore(data.users.length === limitToUse);
          }
        } else {
          setError('Unexpected response format from server');
        }
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    }
  };

  const loadUsers = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
        setCurrentPage(1);
        setUsers([]);
        setLoadedUserIds(new Set());
        setHasMore(true);
        
        // For localhost in dev mode, use mock users instead of API
        if (allowDevBypass) {
          const mockUsers = getMockUsers();
          
          // Apply filters to mock users
          let filteredUsers = mockUsers;
          
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filteredUsers = filteredUsers.filter(user => 
              user.email?.toLowerCase().includes(searchLower) ||
              user.givenName?.toLowerCase().includes(searchLower) ||
              user.familyName?.toLowerCase().includes(searchLower) ||
              user.uniqueId?.toLowerCase().includes(searchLower)
            );
          }
          
          if (filters.authorizationGrade) {
            filteredUsers = filteredUsers.filter(user => 
              user.authorizationGrade === filters.authorizationGrade
            );
          }
          
          if (filters.planType) {
            filteredUsers = filteredUsers.filter(user => 
              user.planType === filters.planType
            );
          }

          // Set total users from filtered results
          setTotalUsers(filteredUsers.length);
          
          // Load first page
          const paginatedUsers = filteredUsers.slice(0, 4);
          setUsers(paginatedUsers);
          setLoadedUserIds(new Set(paginatedUsers.map(u => u.uniqueId)));
          setHasMore(paginatedUsers.length < filteredUsers.length);
          setLoading(false);
          return;
        }

        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'read:users write:users'
        });
       
        const params = new URLSearchParams({
          page: 1,
          limit: 4,
          ...filters
        });

        // Ensure no double slashes in URL
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const finalUrl = `${baseUrl}/api/god/users?${params}`;

        const response = await fetch(finalUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.users) {
            setUsers(data.users);
            setLoadedUserIds(new Set(data.users.map(u => u.uniqueId)));
            
            // Update pagination info
            if (data.pagination) {
              setHasMore(data.pagination.currentPage < data.pagination.totalPages);
              setTotalUsers(data.pagination.totalUsers);
            } else {
              setHasMore(data.users.length === 4);
            }
          } else {
            setError('Unexpected response format from server');
          }
        } else {
          setError('Failed to load users');
        }
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreUsers = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    
    // Call loadUsers with the specific page number
    await loadUsersForPage(nextPage);
    setCurrentPage(nextPage);
    setLoadingMore(false);
  };

  const toggleUserExpansion = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
      // If this user was being edited, close edit mode
      if (selectedUser && selectedUser.uniqueId === userId) {
        setEditMode(false);
        setSelectedUser(null);
      }
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const fetchUserByReferralCode = async (referralCode) => {
    try {
      // Check cache first to avoid redundant API calls
      if (referrerCache.has(referralCode)) {
        return referrerCache.get(referralCode);
      }

      if (allowDevBypass) {
        // For localhost, search in mock users by referral code
        const mockUsers = getMockUsers();
        const user = mockUsers.find(u => u.referralCode === referralCode);
        // Cache the result with size management for production scale
        setReferrerCache(prev => {
          const newCache = new Map(prev);
          // Limit cache size to prevent memory issues with thousands of users
          if (newCache.size > 100) {
            const firstKey = newCache.keys().next().value;
            newCache.delete(firstKey);
          }
          newCache.set(referralCode, user);
          return newCache;
        });
        return user;
      }

      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'read:users write:users'
      });

      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      // Search by referral code - backend now supports referralCode in search
      const response = await fetch(`${baseUrl}/api/god/users?search=${encodeURIComponent(referralCode)}&limit=1`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.users && data.users.length > 0) {
          // Find the user with matching referral code
          const user = data.users.find(u => u.referralCode === referralCode);
          // Cache the result with size management for production scale
          setReferrerCache(prev => {
            const newCache = new Map(prev);
            // Limit cache size to prevent memory issues with thousands of users
            if (newCache.size > 100) {
              const firstKey = newCache.keys().next().value;
              newCache.delete(firstKey);
            }
            newCache.set(referralCode, user);
            return newCache;
          });
          return user;
        }
      }
      
      // Cache null result to prevent repeated failed lookups with size management
      setReferrerCache(prev => {
        const newCache = new Map(prev);
        if (newCache.size > 100) {
          const firstKey = newCache.keys().next().value;
          newCache.delete(firstKey);
        }
        newCache.set(referralCode, null);
        return newCache;
      });
      return null;
    } catch (err) {
      // Cache null result to prevent repeated failed lookups with size management
      setReferrerCache(prev => {
        const newCache = new Map(prev);
        if (newCache.size > 100) {
          const firstKey = newCache.keys().next().value;
          newCache.delete(firstKey);
        }
        newCache.set(referralCode, null);
        return newCache;
      });
      return null;
    }
  };

  const jumpToReferrer = async (referralCode, currentUserUniqueId = null) => {
    // Prevent referrer lookups while pagination is loading to avoid race conditions
    if (loadingMore || loading) {
      return;
    }
    
    // Find the referrer by referralCode in current loaded users
    let referrer = users.find(u => u.referralCode === referralCode);
    
    // Check for self-referral to prevent duplicate addition
    if (currentUserUniqueId && referrer && referrer.uniqueId === currentUserUniqueId) {
      // This is a self-referral, just expand the current user's card
      const newExpanded = new Set(expandedUsers);
      newExpanded.add(referrer.uniqueId);
      setExpandedUsers(newExpanded);
      
      // Scroll to the current user (self)
      setTimeout(() => {
        const element = document.getElementById(`user-${referrer.uniqueId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect to indicate it's a self-referral
          element.style.backgroundColor = '#fffacd'; // Light yellow for self-referral
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 100);
      return;
    }
    
    if (referrer) {
      // Expand the referrer's entry
      const newExpanded = new Set(expandedUsers);
      newExpanded.add(referrer.uniqueId);
      setExpandedUsers(newExpanded);
      
      // Scroll to the referrer
      setTimeout(() => {
        const element = document.getElementById(`user-${referrer.uniqueId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a highlight effect
          element.style.backgroundColor = '#f1f1f1ff';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 100);
    } else {
      // User not in current list, fetch them from API by referral code
      const fetchedReferrer = await fetchUserByReferralCode(referralCode);
      
      if (fetchedReferrer) {
        // CRITICAL FIX: Check if user exists OR will be added by upcoming pagination
        // This prevents the race condition where pagination adds the same user
        const userAlreadyExists = loadedUserIds.has(fetchedReferrer.uniqueId) || 
                                  users.some(u => u.uniqueId === fetchedReferrer.uniqueId);
        
        if (!userAlreadyExists) {
          // Add the referrer to the bottom of the list
          setUsers(prev => {
            // Double-check again within the setter to prevent race conditions
            const stillNotExists = !prev.some(u => u.uniqueId === fetchedReferrer.uniqueId);
            return stillNotExists ? [...prev, fetchedReferrer] : prev;
          });
          setLoadedUserIds(prev => new Set([...prev, fetchedReferrer.uniqueId]));
        }
        
        // Expand and scroll to the referrer (regardless of whether we just added them)
        const newExpanded = new Set(expandedUsers);
        newExpanded.add(fetchedReferrer.uniqueId);
        setExpandedUsers(newExpanded);
        
        setTimeout(() => {
          const element = document.getElementById(`user-${fetchedReferrer.uniqueId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.style.backgroundColor = '#f1f1f1ff';
            setTimeout(() => {
              element.style.backgroundColor = '';
            }, 2000);
          }
        }, 100);
      } else {
        alert(`Referrer with referral code "${referralCode}" not found in the system`);
      }
    }
  };

  const getReferrerDisplayName = (referralCode, currentUser = null) => {
    // DIRECT means user signed up without a referral link
    if (referralCode === 'DIRECT') {
      return 'Direct signup';
    }
    
    // First check if referrer is in currently loaded users by their referral code
    const referrer = users.find(u => u.referralCode === referralCode);
    if (referrer) {
      const name = referrer.givenName || referrer.familyName ? 
        `${referrer.givenName || ''} ${referrer.familyName || ''}`.trim() : 
        referrer.email || 'Unknown User';
      
      return `${name} (${referrer.uniqueId})`;
    }
    
    // If not found in current users, return the referral code with a clickable indicator
    return `${referralCode} (Click to load referrer)`;
  };

  const editUser = async (user) => {
    setSelectedUser({...user, editReason: ''});
    setEditMode(true);
    setError('');
    setSuccess('');
    
    // Ensure the user is expanded when editing
    const newExpanded = new Set(expandedUsers);
    newExpanded.add(user.uniqueId);
    setExpandedUsers(newExpanded);
  };

  const saveUserChanges = async (updates) => {
    try {
      const token = await getAccessTokenSilently();
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      // Single call to update all fields
      const response = await fetch(`${baseUrl}/api/god/users/${selectedUser.uniqueId}/authorization`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authorizationGrade: updates.authorizationGrade,
          planType: updates.planType,
          earlyAdopter: updates.earlyAdopter,
          reason: updates.reason
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      setSuccess('User updated successfully!');
      setEditMode(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    }
  };

  // Load feedbacks when feedbacks tab is active
  useEffect(() => {
    if (activeTab === 'feedbacks') {
      loadFeedbacks();
    }
  }, [activeTab, feedbackFilters]);

  const loadFeedbacks = async () => {
    try {
      setFeedbacksLoading(true);
      setError('');

      // For localhost in dev mode, use mock feedbacks instead of API
      if (allowDevBypass) {
        const mockFeedbacks = getMockFeedbacks();
        
        // Apply filters to mock feedbacks
        let filteredFeedbacks = mockFeedbacks;
        
        if (feedbackFilters.feedbackType) {
          filteredFeedbacks = filteredFeedbacks.filter(feedback => 
            feedback.feedbackType === feedbackFilters.feedbackType
          );
        }
        
        if (feedbackFilters.resolved) {
          const isResolved = feedbackFilters.resolved === 'true';
          filteredFeedbacks = filteredFeedbacks.filter(feedback => 
            feedback.isResolved === isResolved
          );
        }

        setFeedbacks(filteredFeedbacks);
        setFeedbackHasMore(false);
        setFeedbacksLoading(false);
        return;
      }

      const token = await getToken();
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const params = new URLSearchParams({
        page: feedbackPage,
        limit: 20,
        ...feedbackFilters
      });

      const response = await fetch(`${baseUrl}/feedback/admin/all?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load feedbacks');
      }

      const data = await response.json();
      
      if (data.success) {
        setFeedbacks(data.feedbacks);
        setFeedbackHasMore(data.pagination.currentPage < data.pagination.totalPages);
      }
    } catch (err) {
      setError('Failed to load feedbacks: ' + err.message);
    } finally {
      setFeedbacksLoading(false);
    }
  };

  const handleFeedbackUserClick = async (uniqueId) => {
    // Switch to users tab
    setActiveTab('users');
    
    // Find the user in the current list
    const user = users.find(u => u.uniqueId === uniqueId);
    
    if (user) {
      // User already loaded, just expand and scroll
      const newExpanded = new Set(expandedUsers);
      newExpanded.add(uniqueId);
      setExpandedUsers(newExpanded);
      
      setTimeout(() => {
        const element = document.getElementById(`user-${uniqueId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.backgroundColor = '#f1f1f1ff';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }, 100);
    } else {
      // User not loaded, fetch them
      try {
        const token = await getToken();
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const response = await fetch(`${baseUrl}/api/god/users?search=${uniqueId}&limit=1`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.users && data.users.length > 0) {
            const fetchedUser = data.users[0];
            
            // Add user to list if not already present
            const userExists = loadedUserIds.has(fetchedUser.uniqueId) || 
                               users.some(u => u.uniqueId === fetchedUser.uniqueId);
            
            if (!userExists) {
              setUsers(prev => [...prev, fetchedUser]);
              setLoadedUserIds(prev => new Set([...prev, fetchedUser.uniqueId]));
            }
            
            // Expand and scroll
            const newExpanded = new Set(expandedUsers);
            newExpanded.add(fetchedUser.uniqueId);
            setExpandedUsers(newExpanded);
            
            setTimeout(() => {
              const element = document.getElementById(`user-${fetchedUser.uniqueId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.style.backgroundColor = '#f1f1f1ff';
                setTimeout(() => {
                  element.style.backgroundColor = '';
                }, 2000);
              }
            }, 100);
          }
        }
      } catch (err) {
        alert('Could not find user: ' + uniqueId);
      }
    }
  };

  const handleUpdateFeedback = async () => {
    try {
      if (!selectedFeedback) return;

      // For localhost in dev mode, simulate update without API call
      if (allowDevBypass) {
        // Update the feedback in the local state
        setFeedbacks(prev => prev.map(fb => 
          fb._id === selectedFeedback._id 
            ? {
                ...fb,
                isResolved: selectedFeedback.isResolved,
                adminNotes: selectedFeedback.adminNotes,
                resolvedBy: selectedFeedback.isResolved ? 'mock-god-001' : null,
                resolvedAt: selectedFeedback.isResolved ? new Date().toISOString() : null
              }
            : fb
        ));
        
        setSuccess('Feedback updated successfully! (Mock Mode)');
        setSelectedFeedback(null);
        return;
      }

      const token = await getToken();
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const response = await fetch(`${baseUrl}/feedback/admin/${selectedFeedback._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isResolved: selectedFeedback.isResolved,
          adminNotes: selectedFeedback.adminNotes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update feedback');
      }

      setSuccess('Feedback updated successfully!');
      setSelectedFeedback(null);
      loadFeedbacks(); // Reload feedbacks
    } catch (err) {
      setError('Failed to update feedback: ' + err.message);
    }
  };

  // Helper function to get user display name for feedbacks
  const getFeedbackUserDisplayName = (feedback) => {
    // First check if feedback has userName field (from backend or mock)
    if (feedback.userName) {
      return feedback.userName;
    }
    
    // Try to find the user in the loaded users list
    const user = users.find(u => u.uniqueId === feedback.uniqueId);
    if (user) {
      const name = user.givenName || user.familyName ? 
        `${user.givenName || ''} ${user.familyName || ''}`.trim() : 
        null;
      return name || feedback.userEmail || feedback.uniqueId;
    }
    
    // Fallback to email or uniqueId
    return feedback.userEmail || feedback.uniqueId;
  };

  return (
    <div className="god-management-container">
      <div className="god-management-header">
        <button className="back-btn" onClick={goBack} aria-label="Go back"><img src={back} className='backBtnImg' alt="back"></img></button>
        <h2 className='GodPanelTitle'>God Panel &nbsp;&nbsp; <img src={cloud} className='cloudIcon' alt={'God Panel'} /></h2>
      </div>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      {/* Tab Navigation */}
      <div className="god-panel-tabs">
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'feedbacks' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedbacks')}
        >
          Feedbacks
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <>
          <div className="filters">
            <input
              type="text"
              placeholder="Search users..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
            />
            <select
              value={filters.authorizationGrade}
                onChange={(e) => setFilters({...filters, authorizationGrade: e.target.value})}
              >
                <option value="">All Grades</option>
                <option value="God">God</option>
                <option value="Immortal">Immortal</option>
                <option value="Mortal">Mortal</option>
                <option value="Restricted">Restricted</option>
                <option value="Banned">Banned</option>
                <option value="Suspended">Suspended</option>
              </select>
              <select
                value={filters.planType}
                onChange={(e) => setFilters({...filters, planType: e.target.value})}
              >
                <option value="">All Plans</option>
                <option value="Elite">Elite</option>
                <option value="Pro">Pro</option>
                <option value="Basic">Basic</option>
                <option value="Unassigned">Unassigned</option>
              </select>
            </div>

            {users.length > 0 && (
              <div className="user-count">
                {users.length <= totalUsers ? 
                  `Showing ${users.length} of ${totalUsers} users` : 
                  `Showing ${users.length} users (${users.length - totalUsers} additional loaded)`
                }
              </div>
            )}

        <div className="users-list">
          {loading && users.length === 0 ? (
            <div className="loading">Loading system users...</div>
          ) : users.length === 0 ? (
            <div className="loading">No users found matching your criteria.</div>
          ) : (
            <>
              {users.map(user => (
              <div key={user.uniqueId || user._id || user.auth0Id} id={`user-${user.uniqueId}`} className="user-item">
                {/* One-liner display */}
                <div className="user-header" onClick={() => toggleUserExpansion(user.uniqueId)}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleUserExpansion(user.uniqueId);
                    }}
                    className="expand-toggle-btn"
                  >
                    <img 
                      src={expandedUsers.has(user.uniqueId) ? collapseIcon : expandIcon} 
                      alt={expandedUsers.has(user.uniqueId) ? 'Collapse' : 'Expand'}
                      className="expand-collapse-icon"
                    />
                  </button>
                  <div className="user-avatar">
                    {user.picture ? (
                      <img src={user.picture} alt={`${user.givenName} ${user.familyName}`} />
                    ) : (
                      <div className="avatar-placeholder">
                        {(user.givenName?.[0] || user.email?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-basic-info">
                    <div className="user-name">
                      {user.givenName || user.familyName ? 
                        `${user.givenName || ''} ${user.familyName || ''}`.trim() : 
                        user.email || user.uniqueId || 'Unknown User'
                      }
                    </div>
                    <div className="user-email">({user.uniqueId})</div>
                    <div className="user-badges">
                      <span className={`grade-badge ${(user.authorizationGrade || 'unassigned').toLowerCase()}`}>
                        {user.authorizationGrade || 'Unassigned'}
                      </span>
                      <span className={`plan-badge ${(user.planType || 'unassigned').toLowerCase()}`}>
                        {user.planType || 'Unassigned'}
                      </span>
                      {user.earlyAdopter && (
                        <span className="early-adopter-badge">
                          Early Adopter
                        </span>
                      )}
                      {user.hasPaid !== undefined && (
                        <span className={`payment-status ${user.hasPaid ? 'paid' : 'unpaid'}`}>
                          {user.hasPaid ? 'PAID' : 'UNPAID'}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      editUser(user);
                    }}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>

                {/* Expanded view - shows when user is in expandedUsers set */}
                {expandedUsers.has(user.uniqueId) && (
                  <div className="user-details-expanded">
                    {editMode && selectedUser?.uniqueId === user.uniqueId && (
                      <div className="inline-edit-form">
                        <div className="edit-form-header">
                          <h4>Edit User Authorization</h4>
                        </div>
                        <div className="edit-badges">
                          <select 
                            value={selectedUser.authorizationGrade} 
                            onChange={(e) => setSelectedUser({...selectedUser, authorizationGrade: e.target.value})}
                            className="edit-select grade-select"
                          >
                            <option value="God">God</option>
                            <option value="Immortal">Immortal</option>
                            <option value="Mortal">Mortal</option>
                            <option value="Restricted">Restricted</option>
                            <option value="Banned">Banned</option>
                            <option value="Suspended">Suspended</option>
                            <option value="Unassigned">Unassigned</option>
                          </select>
                          <select 
                            value={selectedUser.planType} 
                            onChange={(e) => setSelectedUser({...selectedUser, planType: e.target.value})}
                            className="edit-select plan-select"
                          >
                            <option value="Elite">Elite</option>
                            <option value="Pro">Pro</option>
                            <option value="Basic">Basic</option>
                            <option value="Unassigned">Unassigned</option>
                          </select>
                          <label className="early-adopter-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedUser.earlyAdopter || false}
                              onChange={(e) => setSelectedUser({...selectedUser, earlyAdopter: e.target.checked})}
                            />
                            Early Adopter
                          </label>
                          <div className="badge-preview">
                            {selectedUser.authorizationGrade === 'Immortal' && (
                              <span className="grade-badge immortal">Immortal</span>
                            )}
                            {selectedUser.authorizationGrade === 'Mortal' && (
                              <span className="grade-badge mortal">Mortal</span>
                            )}
                            {selectedUser.planType === 'Elite' && (
                              <span className="plan-badge elite">Elite</span>
                            )}
                            {selectedUser.earlyAdopter && (
                              <span className="early-adopter-badge">
                                Early Adopter
                              </span>
                            )}
                          </div>
                        </div>
                        <textarea
                          value={selectedUser.editReason || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, editReason: e.target.value})}
                          placeholder="Enter reason for this change..."
                          className="edit-reason-textarea"
                          rows={3}
                        />
                        <div className="edit-actions">
                          <button 
                            className="save-btn"
                            onClick={() => {
                              if (!selectedUser.editReason?.trim()) {
                                alert('Please provide a reason for this change');
                                return;
                              }
                              saveUserChanges({
                                authorizationGrade: selectedUser.authorizationGrade,
                                planType: selectedUser.planType,
                                earlyAdopter: selectedUser.earlyAdopter,
                                reason: selectedUser.editReason
                              });
                            }}
                          >
                            Save
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={() => {
                              setEditMode(false);
                              setSelectedUser(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="detail-section">
                      <h4>Contact Information</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <strong>Email:</strong> {user.email || 'Not provided'}
                        </div>
                        <div className="detail-item">
                          <strong>Phone:</strong> {user.phoneNumber || 'Not provided'}
                        </div>
                        <div className="detail-item">
                          <strong>Country:</strong> {user.country || 'Not specified'}
                        </div>
                        <div className="detail-item">
                          <strong>Auth Provider:</strong> 
                          {user.auth0Id?.includes('google') ? ' Google' :
                           user.auth0Id?.includes('facebook') ? ' Facebook' :
                           user.auth0Id?.includes('linkedin') ? ' LinkedIn' :
                           user.auth0Id?.includes('auth0') ? ' Email/Password' : ' Unknown'}
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>Account Status</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <strong>User ID:</strong> {user.uniqueId || user._id}
                        </div>
                        <div className="detail-item">
                          <strong>Member Since:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                        </div>
                        <div className="detail-item">
                          <strong>Email Verified:</strong> {user.isEmailVerified ? '☑️' : '❕'}
                        </div>
                        <div className="detail-item">
                          <strong>Phone Verified:</strong> {user.phoneVerified ? '☑️' : '❕'}
                        </div>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>Referral & Balance</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <strong>Legacy Coins:</strong> {user.legacyCoins || 0}
                        </div>
                        <div className="detail-item">
                          <strong>Referrals Made:</strong> {user.referralCount || 0}
                        </div>
                        <div className="detail-item">
                          <strong>Referral Code:</strong> {user.referralCode || 'None'}
                        </div>
                        <div className="detail-item">
                          <strong>Referred By:</strong> 
                          {user.referredBy && user.referredBy !== 'DIRECT' ? (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                jumpToReferrer(user.referredBy, user.uniqueId);
                              }}
                              className="referrer-link"
                            >
                              {getReferrerDisplayName(user.referredBy, user)}
                            </span>
                          ) : 'Direct signup'}
                        </div>
                      </div>
                    </div>

                    {user.reasonToJoin && (
                      <div className="detail-section">
                        <h4>Reason to Join</h4>
                        <div className="reason-text">{user.reasonToJoin}</div>
                      </div>
                    )}

                    {(user.paymentHistory?.transactions?.length > 0 || user.paymentHistory?.totalPaid > 0) && (
                      <div className="detail-section">
                        <h4>Payment History</h4>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <strong>Total Paid:</strong> £{user.paymentHistory?.totalPaid || 0}
                          </div>
                          <div className="detail-item">
                            <strong>Failed Payments:</strong> {user.paymentHistory?.failedPayments || 0}
                          </div>
                          <div className="detail-item">
                            <strong>Auto-renew:</strong> {user.paymentHistory?.ongoing ? '☑️' : '❕'}
                          </div>
                          <div className="detail-item">
                            <strong>Outstanding Debt:</strong> £{user.paymentHistory?.debt || 0}
                          </div>
                        </div>
                        {user.paymentHistory?.transactions?.length > 0 && (
                          <div className="transaction-list">
                            <strong>Recent Transactions:</strong>
                            {user.paymentHistory.transactions.slice(0, 3).map((transaction, idx) => (
                              <div key={idx} className="transaction-item">
                                {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'Unknown date'}: 
                                £{transaction.amount} - {transaction.status || 'Unknown'}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
            }
            {loadingMore && (
              <div className="loading-more">Loading more users...</div>
            )}
            {!hasMore && users.length > 0 && (
              <div className="no-more-users">No more users to load</div>
            )}
            </>
          )}
        </div>
        </>
      )}

      {/* Feedbacks Tab */}
      {activeTab === 'feedbacks' && (
        <>
          <div className="filters">
            <select
              value={feedbackFilters.feedbackType}
              onChange={(e) => setFeedbackFilters({...feedbackFilters, feedbackType: e.target.value})}
            >
              <option value="">All Types</option>
              <option value="general">General</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="improvement">Improvement</option>
              <option value="question">Question</option>
            </select>
            <select
              value={feedbackFilters.resolved}
              onChange={(e) => setFeedbackFilters({...feedbackFilters, resolved: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>
          </div>

          {feedbacks.length > 0 && (
            <div className="user-count">
              Showing {feedbacks.length} feedback{feedbacks.length !== 1 ? 's' : ''}
            </div>
          )}

          <div className="users-list">
            {feedbacksLoading ? (
              <div className="loading">Loading feedbacks...</div>
            ) : feedbacks.length === 0 ? (
              <div className="loading">No feedbacks found matching your criteria.</div>
            ) : (
              <>
                {feedbacks.map(feedback => (
                  <div key={feedback._id} className="user-item">
                    <div className="user-header" onClick={() => {
                      const newExpanded = new Set(expandedUsers);
                      if (newExpanded.has(feedback._id)) {
                        newExpanded.delete(feedback._id);
                        if (selectedFeedback?._id === feedback._id) {
                          setSelectedFeedback(null);
                        }
                      } else {
                        newExpanded.add(feedback._id);
                      }
                      setExpandedUsers(newExpanded);
                    }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newExpanded = new Set(expandedUsers);
                          if (newExpanded.has(feedback._id)) {
                            newExpanded.delete(feedback._id);
                          } else {
                            newExpanded.add(feedback._id);
                          }
                          setExpandedUsers(newExpanded);
                        }}
                        className="expand-toggle-btn"
                      >
                        <img 
                          src={expandedUsers.has(feedback._id) ? collapseIcon : expandIcon} 
                          alt={expandedUsers.has(feedback._id) ? 'Collapse' : 'Expand'}
                          className="expand-collapse-icon"
                        />
                      </button>
                      <div className="user-avatar">
                        <div className="avatar-placeholder">
                          {feedback.feedbackType[0].toUpperCase()}
                        </div>
                      </div>
                      <div className="user-basic-info">
                        <div className="user-name">
                          {feedback.userEmail}
                        </div>
                        <div className="user-email">({feedback.uniqueId})</div>
                        <div className="user-badges">
                          <span className={`grade-badge ${feedback.feedbackType.toLowerCase()}`}>
                            {feedback.feedbackType}
                          </span>
                          <span className={`plan-badge ${feedback.isResolved ? 'resolved' : 'pending'}`}>
                            {feedback.isResolved ? '✅ Resolved' : '⏳ Pending'}
                          </span>
                          <span className="payment-status" style={{background: '#f0f0f0', color: '#666'}}>
                            {new Date(feedback.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFeedback(feedback);
                        }}
                        className="edit-btn"
                      >
                        {feedback.isResolved ? 'Edit' : 'Resolve'}
                      </button>
                    </div>

                    {expandedUsers.has(feedback._id) && (
                      <div className="user-details-expanded">
                        {selectedFeedback?._id === feedback._id && (
                          <div className="inline-edit-form">
                            <div className="edit-form-header">
                              <h4>Update Feedback Status</h4>
                            </div>
                            <div className="edit-badges">
                              <label className="early-adopter-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedFeedback.isResolved}
                                  onChange={(e) => setSelectedFeedback({
                                    ...selectedFeedback,
                                    isResolved: e.target.checked
                                  })}
                                />
                                Mark as Resolved
                              </label>
                              <div className="badge-preview">
                                {selectedFeedback.isResolved && (
                                  <span className="plan-badge resolved">✅ Resolved</span>
                                )}
                              </div>
                            </div>
                            <textarea
                              value={selectedFeedback.adminNotes || ''}
                              onChange={(e) => setSelectedFeedback({
                                ...selectedFeedback,
                                adminNotes: e.target.value
                              })}
                              placeholder="Add admin notes about this feedback..."
                              className="edit-reason-textarea"
                              rows={3}
                            />
                            <div className="edit-actions">
                              <button 
                                className="save-btn"
                                onClick={handleUpdateFeedback}
                              >
                                Save
                              </button>
                              <button 
                                className="cancel-btn"
                                onClick={() => setSelectedFeedback(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="detail-section">
                          <h4>Feedback Details</h4>
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Type:</strong> {feedback.feedbackType}
                            </div>
                            <div className="detail-item">
                              <strong>Status:</strong> {feedback.isResolved ? 'Resolved ✅' : 'Pending ⏳'}
                            </div>
                            <div className="detail-item">
                              <strong>Submitted:</strong> {new Date(feedback.createdAt).toLocaleString()}
                            </div>
                            <div className="detail-item">
                              <strong>User:</strong>{' '}
                              <span 
                                onClick={() => handleFeedbackUserClick(feedback.uniqueId)}
                                className="referrer-link"
                              >
                                {getFeedbackUserDisplayName(feedback)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h4>Feedback Message</h4>
                          <div className="reason-text">
                            {feedback.feedbackText}
                          </div>
                        </div>

                        {feedback.adminNotes && (
                          <div className="detail-section">
                            <h4>Admin Notes</h4>
                            <div className="reason-text" style={{borderLeft: '4px solid #ffc107', background: '#fff9e6'}}>
                              {feedback.adminNotes}
                            </div>
                          </div>
                        )}

                        {feedback.isResolved && feedback.resolvedAt && (
                          <div className="detail-section">
                            <h4>Resolution Info</h4>
                            <div className="detail-grid">
                              <div className="detail-item">
                                <strong>Resolved At:</strong> {new Date(feedback.resolvedAt).toLocaleString()}
                              </div>
                              {feedback.resolvedBy && (
                                <div className="detail-item">
                                  <strong>Resolved By:</strong> {feedback.resolvedBy}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GodManagement;
